import { IF, NO, UITextView, UIView, YES } from "uicore-ts"
import { RHContractDuration, RHContractDurationType, RHProcurementPart } from "../../../models/ProcurementModels"
import { RHContractDurationLimits, RHContractDurationValidator } from "../ContractDurationRules"
import { CBButtonGroup } from "../Custom components/CB/CBButtonGroup"
import { CBDialogViewShower } from "../Custom components/CB/CBDialogViewShower"
import { RHAutocompleteTextField } from "../Custom components/RH/RHAutocompleteTextField"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHDatePicker } from "../Custom components/RH/RHDatePicker"
import { RHRadioButton } from "../Custom components/RH/RHRadioButton"
import { RHRichTextEditorView } from "../Custom components/RH/RHRichTextEditorView"
import { RHTextField } from "../Custom components/RH/RHTextField"
import { RHCPVCodeFormatter, RHCPVValue } from "../Custom components/RH/Utils_slop/RHCPVCodeFormatter"
import { RHCPVInviteCountCache } from "../Custom components/RH/Utils_slop/RHCPVInviteCountCache"
import { RHButtons, RHColors, RHForms, RHRadius, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHProcurementPartDisplay } from "../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import {
    RHNewProcurementViewTokens,
    RHProcurementPartEditorViewTokens
} from "../Custom components/RH/Utils_slop/RHViewTokens"
import {
    InvitedCompany,
    RHCPVSummationInputPart,
    RHCPVSummationReport,
    RHProcurementTemplateInvitationImportResult,
    RHProcurementTemplateInvitationImportRow
} from "../SocketClientFunctions"
import { CPVSummationPartReportView } from "./CPVSummation/CPVSummationPartReportView"
import { CPVSummationRowInfoProvider, CPVTreeDialogView } from "./CPVTreeDialogView"
import { InvitedCompaniesDialogView } from "./InvitedCompaniesDialogView"
import { ProcurementInvitationState } from "./ProcurementInvitationState"
import {
    ProcurementTemplateLockButton,
    type ProcurementTemplateLockControlConfiguration
} from "./ProcurementTemplateLockButton"


/**
 * A reusable component for editing a single procurement part.
 * Includes fields for: description, CPV code, and estimated cost.
 * (Title has been removed — parts are identified by their number.)
 */
export class ProcurementPartEditorView extends UIView {
    partIdentifier: string = ""
    sourceTemplatePartId?: string
    sourcePart?: Readonly<Partial<RHProcurementPart>>
    isReadOnly = NO
    _partNumber: number = 1
    _partCount: number = 1
    _canRemove: boolean = YES
    removalWasRequested?: () => void
    partDidChange?: () => void
    cpvTreeSelectionDidChange?: () => void
    _cpvCodeForSummation?: string
    _cpvLocked = NO
    _isCpvHidden = NO
    _isDescriptionHidden = NO
    _isEstimatedCostHidden = NO
    _isContractDurationHidden = NO
    _isInvitedCompaniesHidden = NO
    _contractDurationLocked = NO
    _invitedCompaniesLocked = NO
    _canOverrideLockedSupplierSourceCPVs = NO
    _persistsCompanyCustomization = YES
    templateImportResolver?: (
        rows: RHProcurementTemplateInvitationImportRow[]
    ) => Promise<RHProcurementTemplateInvitationImportResult>

    get canRemove(): boolean {
        return this._canRemove
    }

    set canRemove(value: boolean) {
        if (this._canRemove === value) {
            return
        }
        this._canRemove = value
        this.setNeedsLayout()
    }

    get partNumber(): number {
        return this._partNumber
    }

    set partNumber(partNumber: number) {
        this._partNumber = partNumber
        this._updatePartTerminology()
    }

    get partCount(): number {
        return this._partCount
    }

    get cpvCodeForSummation(): string {
        return this._cpvCodeForSummation ?? this.cpvCodeTextField.text
    }

    set partCount(partCount: number) {
        this._partCount = partCount
        this._updatePartTerminology()
    }

    removePartButton = new RHButton().addedAsSubviewToView(this).configuredWithObject({
        titleLabel: { text: RHButtons.deleteIconHTML + " Eemalda osa" },
        variant: RHButtons.destructiveUtilityVariant,
        contentPadding: RHButtons.compactContentPadding,
        style: {
            fontSize: RHButtons.compactFontSize
        },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: (sender, event) => {
                if (this.removalWasRequested) {
                    this.removalWasRequested()
                    this.setNeedsLayoutUpToRootView()
                }
            }
        }
    })

    readonly partHeaderLabel = new UITextView(undefined, UITextView.type.header4)
        .addedAsSubviewToView(this)
        .configuredWithObject({
            text: "Osa 1",
            textColor: RHColors.textPrimary,
            style: {
                // color: RHColor.textPrimary.stringValue
                color: RHColors.textPrimary.stringValue
            }
        })

    // Part fields
    readonly descriptionTemplateLockButton = new ProcurementTemplateLockButton("Kirjeldus")
    readonly estimatedCostTemplateLockButton = new ProcurementTemplateLockButton("Eeldatav maksumus")
    descriptionLabel = new UITextView().addedAsSubviewToView(this)
        .configuredWithObject({
            innerHTML: "Osa kirjeldus <span style=\"color: " + RHColor.danger.stringValue + "\">*</span>",
            style: {
                fontWeight: RHProcurementPartEditorViewTokens.fieldLabelFontWeight
            }
        })
    descriptionEditor = new RHRichTextEditorView().addedAsSubviewToView(this)
        .configuredWithObject({ placeholderText: "Kirjelda seda osa", isRequired: YES })
    descriptionValidationMessageLabel = new UITextView()
        .configuredWithObject({
            textAlignment: UITextView.textAlignment.left,
            textColor: RHColor.danger
        })

    cpvCodeLabel = new UITextView().addedAsSubviewToView(this)
        .configuredWithObject({
            innerHTML: "CPV-kood <span style=\"color: " + RHColor.danger.stringValue + "\">*</span>",
            style: {
                fontWeight: RHProcurementPartEditorViewTokens.fieldLabelFontWeight
            }
        })
    readonly cpvTemplateLockButton = new ProcurementTemplateLockButton("CPV-kood")
    readonly cpvLockIndicator = new UITextView().configuredWithObject({
        text: "Väli on hankemallis lukustatud",
        textColor: RHColor.textSecondary,
        style: { fontSize: "0.85em" }
    })
    cpvCodeTextField = new RHAutocompleteTextField().addedAsSubviewToView(this)
        .configuredWithObject({
            placeholderText: "Sisesta CPV-kood",
            validatesAgainstAutocomplete: YES,
            usesMultiWordAndSearch: YES,
            strictSelection: YES,
            isRequired: YES
        })
    cpvCodeValidationMessageLabel = new UITextView()
        .configuredWithObject({
            textAlignment: UITextView.textAlignment.left,
            textColor: RHColor.danger
        })

    cpvSummationPartReportView = new CPVSummationPartReportView()

    readonly foundCompanyCountLabel = new UITextView()
        .configuredWithObject({
            text: "Leitud ettevõtteid: laadimine…",
            textColor: RHColor.textSecondary,
            style: {
                fontWeight: RHProcurementPartEditorViewTokens.fieldLabelFontWeight
            }
        })

    _inviteCountCache = new RHCPVInviteCountCache()
    summationInfoForCPVCode?: CPVSummationRowInfoProvider

    readonly cpvTreeDialog = new CPVTreeDialogView()

    readonly invitedCompaniesDialog = new InvitedCompaniesDialogView()

    openCPVTreeButton = new RHButton().addedAsSubviewToView(this).configuredWithObject({
        titleLabel: { text: "Ava CPV-puu" },
        variant: RHButtons.compactPrimaryVariant,
        contentPadding: RHButtons.compactPrimaryContentPadding,
        style: {
            fontSize: RHButtons.compactPrimaryFontSize
        },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: async (sender, event) => {
                const currentCode = this.cpvCodeTextField.text.split(" ").firstElement
                const initialCodes = currentCode?.trim() ? [currentCode.trim()] : []

                this.loading = YES
                this._cpvCodeForSummation = currentCode?.trim() ?? ""
                this.cpvTreeSelectionDidChange?.()
                await this.cpvTreeDialog.show(
                    initialCodes,
                    (selectedCodes: string[]) => {
                        if (!selectedCodes.length) {
                            this.setCPVCodeFromSavedValue("")
                            this.partDidChange?.()
                            return
                        }
                        const code = selectedCodes.firstElement
                        this.setCPVCodeFromSavedValue(code)
                        this.partDidChange?.()
                    },
                    () => {
                        this._cpvCodeForSummation = undefined
                        this.cpvTreeSelectionDidChange?.()
                        this.loading = NO
                        // Return focus to the button so the next Tab press
                        // is handled by the managed focus sequence
                        this.openCPVTreeButton.focus()
                    }
                )
            }
        }
    })

    openInvitedCompaniesButton = new RHButton().addedAsSubviewToView(this).configuredWithObject({
        variant: RHButtons.compactPrimaryVariant,
        contentPadding: RHButtons.compactPrimaryContentPadding,
        style: {
            fontSize: RHButtons.compactPrimaryFontSize
        },
        titleLabel: {
            text: "Osalema kutsutavad ettevõtted",
            textAlignment: UITextView.textAlignment.center
        },
        enabled: NO,
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: async (sender, event) => {
                const cpvCode = this.cpvCodeTextField.text.split(" ").firstElement?.trim()
                this.invitedCompaniesDialog.invitedCompaniesView.templateImportResolver =
                    this.templateImportResolver
                this.invitedCompaniesDialog.show(
                    cpvCode,
                    this.additionalInviteSourceCPVCodes,
                    (invitedCompanies, additionalSourceCPVCodes) => {
                        const invitationCPVCodes: string[] = []
                        if (cpvCode) {
                            invitationCPVCodes.push(cpvCode)
                        }
                        // Store the full company list (all isInvited states) so
                        // dialog-local exclusions remain available until its
                        // standing CPV customization save completes. The procurement
                        // snapshot getter projects only selected companies.
                        this.invitedCompanies =
                            this.invitedCompaniesDialog.invitedCompaniesView.allCompanies.map(company => ({
                                ...company,
                                partIds: [this.partIdentifier],
                                cpvCodes: invitationCPVCodes
                            }))
                        this.additionalInviteSourceCPVCodes = [...additionalSourceCPVCodes]
                        // Mark the count as up-to-date for the current CPV code so
                        // _updateInvitedCompaniesButtonEnabled does not trigger a
                        // redundant server fetch that would overwrite this fresh count.
                        this._cpvCodeForLastLoadedCompanyCounts = cpvCode
                        this._updateInvitedCompaniesButtonTitle(invitedCompanies.length)
                        const foundCompanyCount =
                            this._inviteCountCache.cachedFoundCompanyCountForCPVCode(cpvCode ?? "")
                        if (foundCompanyCount !== undefined) {
                            this._showFoundCompanyCount(foundCompanyCount)
                        }
                        this.partDidChange?.()
                    },
                    {
                        supplierCircleLocked: this._invitedCompaniesLocked,
                        canOverrideSourceCPVs: this._canOverrideLockedSupplierSourceCPVs,
                        persistsCompanyCustomization: this._persistsCompanyCustomization
                    }
                )
            }
        }
    })
    readonly invitedCompaniesTemplateLockButton = new ProcurementTemplateLockButton(
        "Osalema kutsutavad ettevõtted"
    )

    readonly invitedCompaniesLockIndicator = new UITextView().configuredWithObject({
        text: "Väli on hankemallis lukustatud",
        textColor: RHColor.textSecondary,
        style: { fontSize: "0.85em" }
    })

    estimatedCostLabel = new UITextView().addedAsSubviewToView(this)
        .configuredWithObject({
            text: "Eeldatav maksumus (KM-ta)",
            style: {
                fontWeight: RHProcurementPartEditorViewTokens.fieldLabelFontWeight
            }
        })

    estimatedCostTextField = new RHTextField().addedAsSubviewToView(this)
        .configuredWithObject({
            placeholderText: "Osa eeldatav maksumus (€)",
            type: "number",
            allowDecimals: YES,
            usesCommaAsDecimalSeparator: YES,
            thousandsSeparator: "\u00A0",
            // Format live while typing — same approach as the offer form's price
            // field (EnterProcurementItemOfferView.priceField).
            showsFormattedDisplayWhileFocused: YES,
            minValue: 0
        })


    configureTemplateAuthoringLocks(options?: {
        description?: ProcurementTemplateLockControlConfiguration
        estimatedCost?: ProcurementTemplateLockControlConfiguration
        cpv: ProcurementTemplateLockControlConfiguration
        invitedCompanies: ProcurementTemplateLockControlConfiguration
        contractDuration?: ProcurementTemplateLockControlConfiguration
    }) {
        this.descriptionEditor.isRequired = !options || !!options.description?.locked
        this.cpvCodeTextField.isRequired = !options || options.cpv.locked
        this.cpvCodeLabel.innerHTML = "CPV-kood" + IF(this.cpvCodeTextField.isRequired)(() =>
            ` <span style="color: ${RHColor.danger.stringValue}">*</span>`
        ).ELSE(() => "")
        if (!this.descriptionEditor.isRequired) {
            this._updateRequiredFieldValidationMessage(this.descriptionValidationMessageLabel)
        }
        if (!this.cpvCodeTextField.isRequired) {
            this._updateRequiredFieldValidationMessage(this.cpvCodeValidationMessageLabel)
        }
        if (options && !options.contractDuration?.locked) {
            this._hasValidatedDuration = NO
            this.contractDurationValidationMessageLabel.removeFromSuperview()
        }
        const isDurationRequired = !options || !!options.contractDuration?.locked
        for (const field of [this.contractDurationMonthsTextField, this.contractDurationDaysTextField,
            this.contractDurationEndDatePicker]) {
            field.isRequired = isDurationRequired
            if (!isDurationRequired) {
                field.resetValidationVisuals()
            }
        }
        for (const [label, title] of [
            [this.durationSectionTitleLabel, "Lepingu kestus"],
            [this.contractDurationMonthsLabel, "Kestus kuudes"],
            [this.contractDurationDaysLabel, "Kestus päevades"],
            [this.contractDurationEndDateLabel, "Lõppkuupäev"]
        ] as const) {
            label.innerHTML = title + IF(isDurationRequired)(() =>
                ` <span style="color: ${RHColor.danger.stringValue}">*</span>`
            ).ELSE(() => "")
        }
        this._updatePartTerminology()
        for (const [button, configuration] of [
            [this.descriptionTemplateLockButton, options?.description],
            [this.estimatedCostTemplateLockButton, options?.estimatedCost]
        ] as const) {
            if (configuration) {
                button.configure(configuration)
                button.addedAsSubviewToView(this)
            }
            else {
                button.removeFromSuperview()
            }
        }
        if (options) {
            this.cpvTemplateLockButton.configure(options.cpv)
            this.cpvTemplateLockButton.addedAsSubviewToView(this)
            this.invitedCompaniesTemplateLockButton.configure(options.invitedCompanies)
            this.invitedCompaniesTemplateLockButton.addedAsSubviewToView(this)
        }
        else {
            this.cpvTemplateLockButton.removeFromSuperview()
            this.invitedCompaniesTemplateLockButton.removeFromSuperview()
        }
        if (options?.contractDuration) {
            this.contractDurationTemplateLockButton.configure(options.contractDuration)
            this.contractDurationTemplateLockButton.addedAsSubviewToView(this)
        }
        else {
            this.contractDurationTemplateLockButton.removeFromSuperview()
        }
        this.setNeedsLayoutUpToRootView()
    }


    configureTemplateProtection(options: {
        isDescriptionLocked?: boolean
        isEstimatedCostLocked?: boolean
        isDescriptionHidden?: boolean
        isEstimatedCostHidden?: boolean
        isCpvHidden?: boolean
        isContractDurationHidden?: boolean
        isInvitedCompaniesHidden?: boolean
        isCpvLocked: boolean
        isContractDurationLocked?: boolean
        isInvitedCompaniesLocked: boolean
        canOverrideLockedSupplierSourceCPVs: boolean
        persistsCompanyCustomization: boolean
    }) {
        this._isDescriptionHidden = !!options.isDescriptionHidden && !!options.isDescriptionLocked
        this._isEstimatedCostHidden = !!options.isEstimatedCostHidden && !!options.isEstimatedCostLocked
        this.descriptionEditor.enabled = !this.isReadOnly && !options.isDescriptionLocked
        this.estimatedCostTextField.configureWithObject({
            enabled: !this.isReadOnly && !options.isEstimatedCostLocked,
            textElementView: { viewHTMLElement: { readOnly: this.isReadOnly || !!options.isEstimatedCostLocked } }
        })
        this._isCpvHidden = !!options.isCpvHidden && options.isCpvLocked
        this._isContractDurationHidden = !!options.isContractDurationHidden && !!options.isContractDurationLocked
        this._isInvitedCompaniesHidden = !!options.isInvitedCompaniesHidden && options.isInvitedCompaniesLocked
        this._cpvLocked = options.isCpvLocked
        this._invitedCompaniesLocked = options.isInvitedCompaniesLocked
        this._canOverrideLockedSupplierSourceCPVs = options.canOverrideLockedSupplierSourceCPVs
        this._persistsCompanyCustomization = options.persistsCompanyCustomization
        this.cpvCodeTextField.configureWithObject({
            enabled: !this.isReadOnly && !options.isCpvLocked,
            textElementView: { viewHTMLElement: { readOnly: this.isReadOnly || options.isCpvLocked } }
        })
        this._contractDurationLocked = this.isReadOnly || !!options.isContractDurationLocked
        for (const control of [
            this.durationSpecifiedInDocsButton,
            this.durationInMonthsButton,
            this.durationInDaysButton,
            this.durationAsDateRangeButton,
            this.contractDurationMonthsTextField,
            this.contractDurationDaysTextField,
            this.contractDurationStartDatePicker,
            this.contractDurationEndDatePicker,
            this.copyDurationFromAboveButton
        ]) {
            control.enabled = !this._contractDurationLocked
        }
        for (const textField of [this.contractDurationMonthsTextField, this.contractDurationDaysTextField]) {
            textField.textElementView.configureWithObject({
                viewHTMLElement: { readOnly: this._contractDurationLocked }
            })
        }
        this.openCPVTreeButton.enabled = !this.isReadOnly && !options.isCpvLocked
        this._updateInvitedCompaniesButtonEnabled()
        if (options.isCpvLocked && !this._isCpvHidden) {
            this.cpvLockIndicator.addedAsSubviewToView(this)
        }
        else {
            this.cpvLockIndicator.removeFromSuperview()
        }
        if (options.isInvitedCompaniesLocked && !this._isInvitedCompaniesHidden) {
            this.invitedCompaniesLockIndicator.text = options.canOverrideLockedSupplierSourceCPVs
                ? "Väli on hankemallis lukustatud; muuta saab ainult CPV-põhist automaatset ringi"
                : "Väli on hankemallis lukustatud"
            this.invitedCompaniesLockIndicator.addedAsSubviewToView(this)
        }
        else {
            this.invitedCompaniesLockIndicator.removeFromSuperview()
        }
        const fields = [
            { isHidden: this._isDescriptionHidden, views: [this.descriptionLabel, this.descriptionEditor] },
            { isHidden: this._isEstimatedCostHidden, views: [this.estimatedCostLabel, this.estimatedCostTextField] },
            { isHidden: this._isCpvHidden, views: [this.cpvCodeLabel, this.cpvCodeTextField, this.openCPVTreeButton] },
            { isHidden: this._isInvitedCompaniesHidden, views: [this.openInvitedCompaniesButton] },
            { isHidden: this._isContractDurationHidden, views: [this.durationSectionTitleLabel,
                this.durationSpecifiedInDocsButton, this.durationInMonthsButton,
                this.durationInDaysButton, this.durationAsDateRangeButton] }
        ]
        for (const field of fields) {
            for (const view of field.views) {
                if (field.isHidden) {
                    view.removeFromSuperview()
                }
                else {
                    view.addedAsSubviewToView(this)
                }
            }
        }
        if (this._isDescriptionHidden) {
            this.descriptionValidationMessageLabel.removeFromSuperview()
        }
        if (this._isCpvHidden) {
            this.cpvCodeValidationMessageLabel.removeFromSuperview()
            this.cpvSummationPartReportView.removeFromSuperview()
        }
        if (this._isInvitedCompaniesHidden) {
            this.foundCompanyCountLabel.removeFromSuperview()
        }
        if (this._isContractDurationHidden) {
            this.copyDurationFromAboveButton.removeFromSuperview()
            this.contractDurationValidationMessageLabel.removeFromSuperview()
        }
        this._updateDurationTypeFields()
        this.setNeedsLayoutUpToRootView()
    }

    // ─── Contract duration (per-part) ────────────────────────────────────────
    readonly contractDurationTemplateLockButton = new ProcurementTemplateLockButton("Lepingu kestus")

    durationSectionTitleLabel = new UITextView(undefined, UITextView.type.header5)
        .addedAsSubviewToView(this)
        .configuredWithObject({
            innerHTML: "Lepingu kestus <span style=\"color: " + RHColor.danger.stringValue + "\">*</span>"
        })

    copyDurationFromAboveButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Kopeeri ülaltoodud kestus" },
            variant: RHButtons.compactPrimaryVariant,
            contentPadding: RHButtons.compactPrimaryContentPadding,
            style: {
                fontSize: RHButtons.compactPrimaryFontSize
            }
        })

    private _updatePartTerminology() {
        this.partHeaderLabel.text = RHProcurementPartDisplay.partHeaderText(
            { partNumber: this._partNumber },
            this._partCount
        )
        if (this.isReadOnly) {
            this.partHeaderLabel.text += " · Ainult vaatamiseks"
        }

        if (this.isReadOnly || RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(this._partCount)) {
            this.partHeaderLabel.hidden = NO
        }
        else {
            this.partHeaderLabel.hidden = YES
        }

        this.descriptionLabel.innerHTML = RHProcurementPartDisplay.descriptionLabelHTML(
            this._partCount, this.descriptionEditor.isRequired
        )
        this.descriptionEditor.placeholderText =
            RHProcurementPartDisplay.descriptionPlaceholderText(this._partCount)
        this.estimatedCostTextField.placeholderText =
            RHProcurementPartDisplay.estimatedCostPlaceholderText(this._partCount)
        this.setNeedsLayoutUpToRootView()
    }

    durationSpecifiedInDocsButton = new RHRadioButton()
        .addedAsSubviewToView(this)
        .configuredWithObject({ titleLabel: { text: "Määratakse hanke alusdokumentides" } })

    durationInMonthsButton = new RHRadioButton()
        .addedAsSubviewToView(this)
        .configuredWithObject({ titleLabel: { text: "Kestus kuudes" } })

    durationInDaysButton = new RHRadioButton()
        .addedAsSubviewToView(this)
        .configuredWithObject({ titleLabel: { text: "Kestus päevades" } })

    durationAsDateRangeButton = new RHRadioButton()
        .addedAsSubviewToView(this)
        .configuredWithObject({ titleLabel: { text: "Kuupäevade vahemik" } })

    contractDurationButtonGroup = new CBButtonGroup({
        "specifiedInDocs": this.durationSpecifiedInDocsButton,
        "durationInMonths": this.durationInMonthsButton,
        "durationInDays": this.durationInDaysButton,
        "dateRange": this.durationAsDateRangeButton
    })

    // Conditional inputs — added/removed dynamically based on radio selection
    contractDurationMonthsLabel = new UITextView()
        .configuredWithObject({
            innerHTML: "Kestus kuudes <span style=\"color: " + RHColor.danger.stringValue + "\">*</span>"
        })
    contractDurationMonthsTextField = new RHTextField()
        .configuredWithObject({
            placeholderText: "Kuude arv",
            type: "number",
            minValue: RHContractDurationLimits.minimumMonths,
            isRequired: YES
        })

    contractDurationDaysLabel = new UITextView()
        .configuredWithObject({
            innerHTML: "Kestus päevades <span style=\"color: " + RHColor.danger.stringValue + "\">*</span>"
        })
    contractDurationDaysTextField = new RHTextField()
        .configuredWithObject({
            placeholderText: "Päevade arv",
            type: "number",
            minValue: RHContractDurationLimits.minimumDays,
            isRequired: YES
        })

    contractDurationStartDateLabel = new UITextView()
        .configuredWithObject({
            // Start date is optional — the backend leaves startDate not-required — so no asterisk.
            innerHTML: "Alguskuupäev"
        })
    contractDurationStartDatePicker = new RHDatePicker()
        .configuredWithObject({
            type: "date",
            minDate: new Date(),
            placeholder: "Vali lepingu alguskuupäev",
            isRequired: NO
        })

    contractDurationEndDateLabel = new UITextView()
        .configuredWithObject({
            innerHTML: "Lõppkuupäev <span style=\"color: " + RHColor.danger.stringValue + "\">*</span>"
        })
    contractDurationEndDatePicker = new RHDatePicker()
        .configuredWithObject({
            type: "date",
            minDate: new Date(),
            placeholder: "Vali lepingu lõppkuupäev",
            isRequired: YES
        })

    contractDurationValidationMessageLabel = new UITextView()
        .configuredWithObject({
            textAlignment: UITextView.textAlignment.left,
            textColor: RHColor.danger
        })

    _hasValidatedDuration = NO


    /** The CPV code for which company counts were last loaded, to avoid redundant fetches. */
    _cpvCodeForLastLoadedCompanyCounts: string | undefined = undefined
    _invitationStateCPVCode: string | undefined
    _invitationRetentionCPVCode?: string
    _cpvCodesWithPendingCompanyCountRefresh = new Set<string>()

    /**
     * All companies in the invited-companies dialog for this part's CPV code,
     * both invited (isInvited === true) and un-invited. Updated each time the
     * InvitedCompaniesDialogView is dismissed. The full list is needed so the
     * server can persist exclusions (isInvited === false) alongside invitations.
     */
    invitedCompanies: InvitedCompany[] = []
    additionalInviteSourceCPVCodes: string[] = []


    constructor(elementID?: string) {

        super(elementID);

        (this as ProcurementPartEditorView).configureWithObject({
            style: {
                // backgroundColor: RHColor.whiteColor.stringValue,
                backgroundColor: RHColors.white.stringValue,
                // borderRadius: "8px",
                borderRadius: `${RHRadius.large}px`,
                // padding: "16px",
                padding: RHSpacing.medium + "px",
                // border: `1px solid ${RHColor.border.stringValue}`
                border: `1px solid ${RHColors.borderLight.stringValue}`
            }
        })

        this.invitedCompaniesDialog.invitedCountDidChange = (count: number) => {
            this._updateInvitedCompaniesButtonTitle(count)
        }
        this.invitedCompaniesDialog.inviteCountCache = this._inviteCountCache
        this.cpvTreeDialog.foundCompanyCountForCPVCode = (cpvCode, shouldLoad) =>
            this._foundCompanyCountForCPVCode(cpvCode, shouldLoad)
        this.cpvTreeDialog.inviteCountForCPVCode = (cpvCode, shouldLoad) =>
            this._inviteCountForCPVCode(cpvCode, shouldLoad)
        this.cpvTreeDialog.summationInfoForCPVCode = (cpvCode, shouldLoad) =>
            this.summationInfoForCPVCode?.(cpvCode, shouldLoad)
        this.cpvTreeDialog.selectionDidChange = selectedCodes => {
            this._cpvCodeForSummation = selectedCodes[0] ?? ""
            this.cpvTreeSelectionDidChange?.()
        }
        this.cpvCodeTextField.autocompleteDisplayLabelForItem = item =>
            this._cpvAutocompleteDisplayLabelForItem(item.label)

        this.cpvCodeTextField.controlEventTargetAccumulator.TextChange = () => {
            this._updateInvitedCompaniesButtonEnabled()
        }

        this.cpvCodeTextField.controlEventTargetAccumulator.SelectionDidChange = () => {
            this._updateInvitedCompaniesButtonEnabled()
        }

        this.contractDurationButtonGroup.configureWithObject({
            selectionDidChange: () => {
                this._updateDurationTypeFields()
                if (this._hasValidatedDuration) {
                    this.validateDuration()
                }
                this.partDidChange?.()
                this.setNeedsLayoutUpToRootView()
            }
        })

        this.contractDurationMonthsTextField.controlEventTargetAccumulator.TextChange = () => {
            if (this._hasValidatedDuration) {
                this.validateDuration()
            }
            this.partDidChange?.()
        }

        this.contractDurationDaysTextField.controlEventTargetAccumulator.TextChange = () => {
            if (this._hasValidatedDuration) {
                this.validateDuration()
            }
            this.partDidChange?.()
        }

        const durationDateDidChange = () => {
            if (this._hasValidatedDuration) {
                this.validateDuration()
            }
            this.partDidChange?.()
        }
        this.contractDurationStartDatePicker.addTargetForControlEvent(
            RHDatePicker.controlEvent.DateChange,
            durationDateDidChange
        )
        this.contractDurationEndDatePicker.addTargetForControlEvent(
            RHDatePicker.controlEvent.DateChange,
            durationDateDidChange
        )

        this._updatePartTerminology()

    }


    override wasAddedToViewTree() {
        super.wasAddedToViewTree()
        this.loadAutocompleteData()
    }


    private static async _loadCPVAutocompleteData() {
        return RHCPVCodeFormatter.autocompleteStrings()
    }


    private _autocompleteLabelForCPVText(cpvText: string) {
        const cpvCode = cpvText.split(" ").firstElement?.trim()
        if (!cpvCode) {
            return undefined
        }

        return this.cpvCodeTextField.autocompleteStrings.find(
            (value: string) => value.startsWith(cpvCode + " ")
        ) ?? this.cpvCodeTextField.autocompleteStrings.find(
            (value: string) => value.startsWith(cpvCode)
        )
    }


    setCPVCodeFromSavedValue(cpvValue: RHCPVValue) {
        const cpvText = (cpvValue ?? "").toString().trim()
        if (!cpvText) {
            this.cpvCodeTextField.text = ""
            this.cpvCodeTextField.resetValidationVisuals()
        }
        else {
            this.cpvCodeTextField.text = this._autocompleteLabelForCPVText(cpvText) ?? cpvText
            this.cpvCodeTextField.updateValidationVisuals()
        }
        this._updateInvitedCompaniesButtonEnabled()
        if (!cpvText) {
            this.clearCPVSummationReport()
        }
        if (this.cpvCodeValidationMessageLabel.superview === this) {
            this.validateCPVCode()
        }
    }


    updateCPVSummationReport(
        report: RHCPVSummationReport | undefined,
        part: RHCPVSummationInputPart | undefined
    ) {
        if (this.cpvSummationPartReportView.updateWithReportForPart(report, part) && !this._isCpvHidden) {
            this.cpvSummationPartReportView.addedAsSubviewToView(this)
        }
        else {
            this.clearCPVSummationReport()
        }
        this.setNeedsLayoutUpToRootView()
    }


    clearCPVSummationReport() {
        this.cpvSummationPartReportView.clearReport()
        this.cpvSummationPartReportView.removeFromSuperview()
        this.setNeedsLayoutUpToRootView()
    }


    normalizeCPVCodeText() {
        const currentText = this.cpvCodeTextField.text.trim()
        if (!currentText) {
            return
        }

        const autocompleteLabel = this._autocompleteLabelForCPVText(currentText)
        if (autocompleteLabel && autocompleteLabel !== currentText) {
            this.cpvCodeTextField.text = autocompleteLabel
        }

        this.cpvCodeTextField.updateValidationVisuals()
    }


    async prepareCPVCodeForValidation() {
        try {
            await this.loadAutocompleteData()
        }
        catch (_error) {
            // Validation below will keep the field invalid if the list could not be loaded.
        }
        this.normalizeCPVCodeText()
    }

    async loadAutocompleteData() {
        const data = await ProcurementPartEditorView._loadCPVAutocompleteData()
        this.cpvCodeTextField.autocompleteStrings = data
        this.cpvCodeTextField.refreshAutocompleteDisplay()
        this.normalizeCPVCodeText()
        this._updateInvitedCompaniesButtonEnabled()
    }


    _updateInvitedCompaniesButtonEnabled() {
        const cpvCode = this.cpvCodeTextField.text.split(" ").firstElement?.trim() ?? ""
        if (this.isReadOnly) {
            this.openInvitedCompaniesButton.enabled = NO
        }
        this._synchronizeInvitationStateWithCPVCode(cpvCode)
        if (this._invitationRetentionCPVCode) {
            return
        }
        const hasCPVCode = cpvCode.length > 0
        this.openInvitedCompaniesButton.enabled = hasCPVCode && !this.isReadOnly
        if (hasCPVCode && cpvCode !== this._cpvCodeForLastLoadedCompanyCounts) {
            this._cpvCodeForLastLoadedCompanyCounts = cpvCode
            this._showFoundCompanyCountLoading()
            this._loadCompanyCountsForCPVCode(cpvCode)
        }
        else if (!hasCPVCode) {
            this._cpvCodeForLastLoadedCompanyCounts = undefined
            this._updateInvitedCompaniesButtonTitle(undefined)
            this._hideFoundCompanyCount()
            this.clearCPVSummationReport()
        }
    }


    _synchronizeInvitationStateWithCPVCode(cpvCode: string) {
        if (this._invitationRetentionCPVCode) {
            return
        }
        const currentCPV8 = RHCPVCodeFormatter.normalizedCPV8FromText(cpvCode)
        const previousCPV8 = RHCPVCodeFormatter.normalizedCPV8FromText(this._invitationStateCPVCode)
        if (!currentCPV8 || !previousCPV8 || currentCPV8 === previousCPV8) {
            if (currentCPV8) {
                this._invitationStateCPVCode = cpvCode
            }
            return
        }
        const hasCPVSelectedInvitees = this.invitedCompanies.some(company =>
            company.isInvited &&
            !ProcurementInvitationState.normalizedSourcesForCompany(company).includes("manually-added")
        )
        if (hasCPVSelectedInvitees && !this._invitedCompaniesLocked) {
            this._askWhetherToRetainInviteesForCPVChange(this._invitationStateCPVCode!, cpvCode)
            return
        }
        this._invitationStateCPVCode = cpvCode
        this._retainInvitedCompaniesForCPVCode(cpvCode, YES)
    }


    _askWhetherToRetainInviteesForCPVChange(previousCPVCode: string, cpvCode: string) {
        this._invitationRetentionCPVCode = cpvCode
        const cpvCodeIsStillDisplayed = () =>
            RHCPVCodeFormatter.normalizedCPV8FromText(this.cpvCodeTextField.text) ===
            RHCPVCodeFormatter.normalizedCPV8FromText(cpvCode)
        const shower = CBDialogViewShower.showQuestionDialogWithCloseButton(
            "Kas soovid jätta alles eelmise CPV-koodi alusel kutsutavad ettevõtted? Manuaalselt lisatud ettevõtted jäävad mõlemal juhul alles.",
            () => {
                if (this._invitationRetentionCPVCode !== cpvCode) {
                    return
                }
                this._invitationRetentionCPVCode = undefined
                if (!this.isMemberOfViewTree || !cpvCodeIsStillDisplayed()) {
                    if (this.isMemberOfViewTree) {
                        this._updateInvitedCompaniesButtonEnabled()
                    }
                    return
                }
                this.setCPVCodeFromSavedValue(previousCPVCode)
                this.partDidChange?.()
            }
        )
        shower.dialogView.view.configureWithObject({
            yesButton: { titleLabel: { text: "Jah" } },
            noButton: { titleLabel: { text: "Ei" } },
            noButtonDismissesDialog: NO
        })
        const applyDecision = (shouldRetainCPVCompanies: boolean) => {
            const wasPending = this._invitationRetentionCPVCode === cpvCode
            if (wasPending) {
                this._invitationRetentionCPVCode = undefined
            }
            if (wasPending && this.isMemberOfViewTree && cpvCodeIsStillDisplayed()) {
                this._invitationStateCPVCode = cpvCode
                this._retainInvitedCompaniesForCPVCode(cpvCode, shouldRetainCPVCompanies)
            }
            if (this.isMemberOfViewTree) {
                this._updateInvitedCompaniesButtonEnabled()
            }
            shower.dialogView.dismiss()
        }
        shower.yesButtonWasPressed = () => applyDecision(YES)
        shower.noButtonWasPressed = () => applyDecision(NO)
    }


    _retainInvitedCompaniesForCPVCode(cpvCode: string, shouldRetainCPVCompanies: boolean) {
        this.additionalInviteSourceCPVCodes = []
        this.invitedCompanies = this.invitedCompanies
            .filter(company => company.isInvited && (shouldRetainCPVCompanies ||
                ProcurementInvitationState.normalizedSourcesForCompany(company).includes("manually-added")))
            .map(company => ({
                ...company,
                partIds: [this.partIdentifier],
                cpvCodes: [cpvCode]
            }))
        this.invitedCompaniesDialog.invitedCompaniesView.loadWithCompanies(
            this.invitedCompanies,
            YES
        )
        this._updateInvitedCompaniesButtonTitle(this.invitedCompanies.filter(company => company.isInvited).length)
        this.partDidChange?.()
    }


    async _loadCompanyCountsForCPVCode(cpvCode: string) {
        try {
            const serverCompanies = await this._inviteCountCache.retrieveCompaniesForCPVCode(cpvCode)
            // Guard: CPV code may have changed while the request was in flight
            if (cpvCode !== this._cpvCodeForLastLoadedCompanyCounts) {
                return
            }
            // Seed the dialog's in-memory list from the server response so its
            // selectedCount reflects reality even before the dialog is first opened.
            // The dialog will re-fetch and merge on open, preserving in-memory toggles.
            this.invitedCompaniesDialog._seedCompanies(serverCompanies, cpvCode)

            // Also populate the editor's own invitedCompanies list from the server
            // response when it is still empty — this ensures the full invited list is
            // included in saveDraft() even if the user never opens the dialog.
            // We only do this when the list is empty to avoid clobbering changes the
            // user has already made inside the dialog during this session.
            // Stamp cpvCodes immediately so the merge in ProcurementPartsEditorView
            // can correctly attribute each company to this part's CPV code.
            if (this.invitedCompanies.length === 0) {
                const normalizedCompanies = this.invitedCompaniesDialog.invitedCompaniesView.allCompanies
                this.invitedCompanies = normalizedCompanies.map(company => ({
                    ...company,
                    partIds: [this.partIdentifier],
                    cpvCodes: company.cpvCodes && company.cpvCodes.length > 0
                              ? company.cpvCodes
                              : [cpvCode]
                }))
            }

            this._updateInvitedCompaniesButtonTitle(this.invitedCompaniesDialog.selectedCount)
            this._showFoundCompanyCount(serverCompanies.length)
        }
        catch (_error) {
            if (cpvCode === this._cpvCodeForLastLoadedCompanyCounts) {
                this._showFoundCompanyCountLoadError()
            }
        }
    }


    _inviteCountForCPVCode(cpvCode: string, shouldLoad = YES): number | undefined {
        if (this._isCurrentCPVCode(cpvCode)) {
            return this.invitedCompaniesDialog.selectedCount
        }

        const cachedCount = this._inviteCountCache.cachedInvitedCompanyCountForCPVCode(cpvCode)
        if (cachedCount !== undefined) {
            return cachedCount
        }

        this._scheduleCompanyCountRefresh(cpvCode, shouldLoad)
        return undefined
    }


    _foundCompanyCountForCPVCode(cpvCode: string, shouldLoad = YES): number | undefined {
        const cachedCount = this._inviteCountCache.cachedFoundCompanyCountForCPVCode(cpvCode)
        if (cachedCount !== undefined) {
            return cachedCount
        }

        this._scheduleCompanyCountRefresh(cpvCode, shouldLoad)
        return undefined
    }


    _scheduleCompanyCountRefresh(cpvCode: string, shouldLoad: boolean) {
        if (shouldLoad == NO || this._cpvCodesWithPendingCompanyCountRefresh.has(cpvCode)) {
            return
        }
        this._cpvCodesWithPendingCompanyCountRefresh.add(cpvCode)

        void this._inviteCountCache.retrieveFoundCompanyCountForCPVCode(cpvCode).then(foundCompanyCount => {
            this.cpvTreeDialog.refreshDisplayedRows()
            this.cpvCodeTextField.refreshAutocompleteDisplay()
            if (this._isCurrentCPVCode(cpvCode)) {
                this._showFoundCompanyCount(foundCompanyCount)
                this._updateInvitedCompaniesButtonTitle(
                    this.invitedCompaniesDialog.selectedCount
                )
            }
        }).catch(() => {
            if (this._isCurrentCPVCode(cpvCode)) {
                this._showFoundCompanyCountLoadError()
            }
        }).finally(() => {
            this._cpvCodesWithPendingCompanyCountRefresh.delete(cpvCode)
        })
    }


    _isCurrentCPVCode(cpvCode: string): boolean {
        const currentCPVCode = this.cpvCodeTextField.text.split(" ").firstElement?.trim()
        const currentCPV8 = RHCPVCodeFormatter.normalizedCPV8FromText(currentCPVCode)
        const candidateCPV8 = RHCPVCodeFormatter.normalizedCPV8FromText(cpvCode)
        return currentCPV8.length > 0 && currentCPV8 === candidateCPV8
    }


    _cpvAutocompleteDisplayLabelForItem(label: string): string {
        const cpvCode = RHCPVCodeFormatter.codeFromCPVText(label)
        if (!cpvCode) {
            return label
        }

        const foundCompanyCount = this._foundCompanyCountForCPVCode(cpvCode)
        if (foundCompanyCount === undefined) {
            return label
        }

        return `${label} (Leitud ettevõtteid: ${foundCompanyCount})`
    }


    /**
     * Restores the invited companies for this part from a previously saved
     * procurement document. Populates `invitedCompanies`, seeds the dialog's
     * in-memory list, and updates the button count badge — all without triggering
     * a server fetch.
     *
     * Call this after loading a draft, passing the procurement-level
     * `invitedCompanies` array filtered to only companies relevant to this part's
     * CPV code (i.e. those whose `cpvCodes` array contains this part's CPV code,
     * or all of them for single-part procurements where no cpvCodes filtering exists).
     */
    restoreInvitedCompaniesFromSavedList(companies: InvitedCompany[]) {
        const cpvCode = this.cpvCodeTextField.text.split(" ").firstElement?.trim()
        let restoredCPVCodes: string[] = []
        if (cpvCode) {
            restoredCPVCodes = [cpvCode]
        }
        this.invitedCompanies = companies.map(company => ({
            ...company,
            partIds: [this.partIdentifier],
            cpvCodes: [...restoredCPVCodes]
        }))
        // An empty saved snapshot is authoritative; otherwise CPV suggestions are selected again.
        this.invitedCompaniesDialog.invitedCompaniesView.loadWithCompanies(
            this.invitedCompanies,
            YES
        )
        // Mark the CPV code as already loaded so _updateInvitedCompaniesButtonEnabled
        // does not fire a redundant server fetch that would overwrite the restored data.
        this._invitationStateCPVCode = cpvCode
        this._cpvCodeForLastLoadedCompanyCounts = cpvCode
        this._updateInvitedCompaniesButtonTitle(companies.filter(company => company.isInvited).length)
        if (cpvCode) {
            const foundCompanyCount =
                this._inviteCountCache.cachedFoundCompanyCountForCPVCode(cpvCode)
            if (foundCompanyCount !== undefined) {
                this._showFoundCompanyCount(foundCompanyCount)
            }
            else {
                this._showFoundCompanyCountLoading()
                this._scheduleCompanyCountRefresh(cpvCode, YES)
            }
        }
        else {
            this._hideFoundCompanyCount()
        }
    }


    restoreAdditionalInviteSourceCPVCodes(cpvCodes: string[]) {
        const baseCPVCode = this.cpvCodeTextField.text.split(" ").firstElement?.trim()
        this.additionalInviteSourceCPVCodes =
            ProcurementInvitationState.normalizedAdditionalSourceCPVCodes(
                baseCPVCode,
                cpvCodes
            )
    }


    _updateInvitedCompaniesButtonTitle(count: number | undefined) {
        const base = "Osalema kutsutavad ettevõtted"
        this.openInvitedCompaniesButton.titleLabel.text = base
        if (count !== undefined) {
            this.openInvitedCompaniesButton.titleLabel.text = `${base} (${count})`
        }
        this.openInvitedCompaniesButton.setNeedsLayout()
        this.setNeedsLayoutUpToRootView()
    }


    _showFoundCompanyCountLoading() {
        this.foundCompanyCountLabel.text = "Leitud ettevõtteid: laadimine…"
        if (!this._isInvitedCompaniesHidden) {
            this.foundCompanyCountLabel.addedAsSubviewToView(this)
        }
        this.setNeedsLayoutUpToRootView()
    }


    _showFoundCompanyCount(count: number) {
        this.foundCompanyCountLabel.text = `Leitud ettevõtteid: ${count}`
        if (!this._isInvitedCompaniesHidden) {
            this.foundCompanyCountLabel.addedAsSubviewToView(this)
        }
        this.setNeedsLayoutUpToRootView()
    }


    _showFoundCompanyCountLoadError() {
        this.foundCompanyCountLabel.text = "Leitud ettevõtteid: laadimine ebaõnnestus"
        if (!this._isInvitedCompaniesHidden) {
            this.foundCompanyCountLabel.addedAsSubviewToView(this)
        }
        this.setNeedsLayoutUpToRootView()
    }


    _hideFoundCompanyCount() {
        this.foundCompanyCountLabel.removeFromSuperview()
        this.setNeedsLayoutUpToRootView()
    }

    private _actionButtonWidth(button: RHButton): number {
        const measuredWidth = button.titleLabel.intrinsicContentWidth(
                RHButtons.compactPrimaryHeight
            )
            + RHButtons.compactPrimaryContentPadding * 2
            + RHSpacing.medium

        return Math.min(
            360,
            Math.max(
                232,
                measuredWidth
            )
        )
    }


    /**
     * Adds or removes the value-input fields based on the current radio selection.
     */
    private _updateDurationTypeFields() {

        const durationType = this.contractDurationButtonGroup.value as RHContractDurationType

        const showMonths = !this._isContractDurationHidden && durationType === "durationInMonths"
        const showDays = !this._isContractDurationHidden && durationType === "durationInDays"
        const showDateRange = !this._isContractDurationHidden && durationType === "dateRange"

        if (showMonths) {
            this.contractDurationMonthsLabel.addedAsSubviewToView(this)
            this.contractDurationMonthsTextField.addedAsSubviewToView(this)
        }
        else {
            this.contractDurationMonthsLabel.removeFromSuperview()
            this.contractDurationMonthsTextField.removeFromSuperview()
        }

        if (showDays) {
            this.contractDurationDaysLabel.addedAsSubviewToView(this)
            this.contractDurationDaysTextField.addedAsSubviewToView(this)
        }
        else {
            this.contractDurationDaysLabel.removeFromSuperview()
            this.contractDurationDaysTextField.removeFromSuperview()
        }

        if (showDateRange) {
            this.contractDurationStartDateLabel.addedAsSubviewToView(this)
            this.contractDurationStartDatePicker.addedAsSubviewToView(this)
            this.contractDurationEndDateLabel.addedAsSubviewToView(this)
            this.contractDurationEndDatePicker.addedAsSubviewToView(this)
        }
        else {
            this.contractDurationStartDateLabel.removeFromSuperview()
            this.contractDurationStartDatePicker.removeFromSuperview()
            this.contractDurationEndDateLabel.removeFromSuperview()
            this.contractDurationEndDatePicker.removeFromSuperview()
        }

    }


    /** Returns the per-part contract duration. */
    get contractDuration(): RHContractDuration | null {
        const durationType = this.contractDurationButtonGroup.value

        if (!durationType) {
            return null
        }

        if (durationType === "specifiedInDocs") {
            return { type: "specifiedInDocs" }
        }

        if (durationType === "durationInMonths") {
            return {
                type: "durationInMonths",
                months: this.contractDurationMonthsTextField.text.numericalValue || undefined
            }
        }

        if (durationType === "durationInDays") {
            return {
                type: "durationInDays",
                days: this.contractDurationDaysTextField.text.numericalValue || undefined
            }
        }

        return {
            type: "dateRange",
            startDate: this.contractDurationStartDatePicker.selectedDate || undefined,
            endDate: this.contractDurationEndDatePicker.selectedDate || undefined
        }

    }


    /** Populates the per-part duration UI from a saved value. */
    set contractDuration(value: RHContractDuration | null | undefined) {
        if (!value) {
            this.contractDurationButtonGroup.value = ""
            this.contractDurationMonthsTextField.text = ""
            this.contractDurationDaysTextField.text = ""
            this.contractDurationStartDatePicker.selectedDate = undefined
            this.contractDurationEndDatePicker.selectedDate = undefined
            this._updateDurationTypeFields()
            return
        }

        this.contractDurationButtonGroup.value = value.type
        this.contractDurationMonthsTextField.text = value.months?.toString() ?? ""
        this.contractDurationDaysTextField.text = value.days?.toString() ?? ""
        this.contractDurationStartDatePicker.selectedDate = undefined
        if (value.startDate) {
            this.contractDurationStartDatePicker.selectedDate = new Date(value.startDate)
        }
        this.contractDurationEndDatePicker.selectedDate = undefined
        if (value.endDate) {
            this.contractDurationEndDatePicker.selectedDate = new Date(value.endDate)
        }
        this._updateDurationTypeFields()

    }


    contractDurationValidationMessage(): string | undefined {
        return RHContractDurationValidator.validationMessageForContractDuration(
            this.contractDuration ?? undefined,
            {
                isRequired: YES,
                partLabel: `Hanke osal ${this.partNumber}`
            }
        )
    }


    inlineContractDurationValidationMessage(): string | undefined {
        return RHContractDurationValidator.validationMessageForContractDuration(
            this.contractDuration ?? undefined,
            {
                isRequired: YES,
                partLabel: "Hankeosal"
            }
        )
    }


    validateDescription(): boolean {
        const isValid = this.descriptionEditor.validate()
        let validationMessage: string | undefined
        if (!isValid) {
            validationMessage = "Hankeosa kirjeldus on kohustuslik."
        }
        this._updateRequiredFieldValidationMessage(
            this.descriptionValidationMessageLabel,
            validationMessage
        )
        return isValid
    }


    validateCPVCode(): boolean {
        const isValid = this.cpvCodeTextField.validate()
        let validationMessage: string | undefined
        if (!isValid) {
            if (this.cpvCodeTextField.text.trim().length > 0) {
                validationMessage = "Vali CPV-kood CPV-loendist."
            }
            else {
                validationMessage = "Hankeosa CPV-kood on kohustuslik."
            }
        }
        this._updateRequiredFieldValidationMessage(
            this.cpvCodeValidationMessageLabel,
            validationMessage
        )
        return isValid
    }


    _updateRequiredFieldValidationMessage(
        label: UITextView,
        validationMessage?: string
    ) {
        const wasVisible = label.superview === this
        if (validationMessage &&
            !(label === this.cpvCodeValidationMessageLabel && this._isCpvHidden) &&
            !(label === this.descriptionValidationMessageLabel && this._isDescriptionHidden)) {
            label.text = validationMessage
            label.addedAsSubviewToView(this)
        }
        else if (wasVisible) {
            label.removeFromSuperview()
        }
        if (wasVisible !== !!validationMessage) {
            this.setNeedsLayoutUpToRootView()
        }
    }


    /** Validates the per-part duration fields. */
    validateDuration(): boolean {
        this._hasValidatedDuration = YES
        const durationType = this.contractDurationButtonGroup.value
        let isValid = YES

        if (!durationType) {
            isValid = NO
        }
        else if (durationType === "durationInMonths") {
            isValid = this.contractDurationMonthsTextField.validate()
        }
        else if (durationType === "durationInDays") {
            isValid = this.contractDurationDaysTextField.validate()
        }
        else if (durationType === "dateRange") {
            const isStartDateValid = this.contractDurationStartDatePicker.validate()
            const isEndDateValid = this.contractDurationEndDatePicker.validate()
            isValid = isStartDateValid && isEndDateValid
        }

        const validationMessage = this.inlineContractDurationValidationMessage()
        const wasValidationMessageVisible = this.contractDurationValidationMessageLabel.superview === this
        if (!isValid && validationMessage && !this._isContractDurationHidden) {
            this.contractDurationValidationMessageLabel.text = validationMessage
            this.contractDurationValidationMessageLabel.addedAsSubviewToView(this)
            this.setNeedsLayoutUpToRootView()
        }
        else if (wasValidationMessageVisible) {
            this.contractDurationValidationMessageLabel.removeFromSuperview()
            this.setNeedsLayoutUpToRootView()
        }

        return isValid

    }


    firstInvalidDurationField(): UIView | undefined {
        const durationType = this.contractDurationButtonGroup.value

        if (!durationType) {
            return this.durationSpecifiedInDocsButton
        }

        if (durationType === "specifiedInDocs") {
            return undefined
        }

        if (durationType === "durationInMonths") {
            if (!this.contractDurationMonthsTextField.validate()) {
                return this.contractDurationMonthsTextField
            }
            return undefined
        }

        if (durationType === "durationInDays") {
            if (!this.contractDurationDaysTextField.validate()) {
                return this.contractDurationDaysTextField
            }
            return undefined
        }

        if (!this.contractDurationStartDatePicker.validate()) {
            return this.contractDurationStartDatePicker
        }

        if (!this.contractDurationEndDatePicker.validate()) {
            return this.contractDurationEndDatePicker
        }

        return undefined

    }


    override layoutSubviews() {

        super.layoutSubviews()

        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const fieldHeight = RHForms.fieldSingleLineFieldHeight
        const bounds = this.contentBoundsWithInset(padding)
            // Absolute-positioned subviews inside a padded card need the horizontal origin
            // pulled back so the visible left/right insets stay symmetrical.
            .rectangleByAddingX(-padding).rectangleByAddingY(-padding)

        if (this.canRemove) {
            this.removePartButton.hidden = NO
        }
        else {
            this.removePartButton.hidden = YES
        }
        this.removePartButton.enabled = this.canRemove

        // Header row: keep the part title on its own top row and anchor the
        // remove action compactly to the upper-right corner instead of letting
        // the row-splitting helper stretch it downward toward the textarea.
        let headerRowHeight = 0
        let descriptionTopGap = 0
        if (this.partHeaderLabel.hidden === NO || this.canRemove) {
            headerRowHeight = labelHeight
            descriptionTopGap = padding
        }
        const headerRowFrame = bounds.rectangleWithHeight(headerRowHeight)

        if (this.canRemove) {
            const partHeaderLabelFrame = headerRowFrame
                .rectangleWithWidth(
                    headerRowFrame.width
                    - RHProcurementPartEditorViewTokens.removePartButtonWidth
                    - padding
                )

            partHeaderLabelFrame
                .assignedAsFrameOfView(this.partHeaderLabel)

            partHeaderLabelFrame
                .rectangleForNextColumn(
                    padding,
                    RHProcurementPartEditorViewTokens.removePartButtonWidth
                )
                .assignedAsFrameOfView(this.removePartButton)
        }
        else {
            headerRowFrame
                .assignedAsFrameOfView(this.partHeaderLabel)
                .assignedAsFrameOfView(this.removePartButton)
        }

        const templateLockControlSize = RHNewProcurementViewTokens.templateLockControl.size
        headerRowFrame
            .IF(!this._isDescriptionHidden)
            .rectangleForNextRow(descriptionTopGap, Math.max(labelHeight, templateLockControlSize))
            .performingFunctionWithSelf(rowFrame =>
                this.descriptionTemplateLockButton.layoutBesideTitleLabelInFrame(this.descriptionLabel, rowFrame)
            )
            .rectangleForNextRow(padding * 0.25, 200)
            .assignedAsFrameOfView(this.descriptionEditor)
            .IF(this.descriptionValidationMessageLabel.superview === this)
            .rectangleForNextRow(RHSpacing.tiny, this.descriptionValidationMessageLabel)
            .assignedAsFrameOfView(this.descriptionValidationMessageLabel)
            .ENDIF()
            .ENDIF()
            .IF(!this._isEstimatedCostHidden)
            .rectangleForNextRow(padding, Math.max(labelHeight, templateLockControlSize))
            .performingFunctionWithSelf(rowFrame =>
                this.estimatedCostTemplateLockButton.layoutBesideTitleLabelInFrame(this.estimatedCostLabel, rowFrame)
            )
            .rectangleForNextRow(padding * 0.25, fieldHeight)
            .assignedAsFrameOfView(this.estimatedCostTextField)
            .ENDIF()

            .IF(!this._isCpvHidden)
            .rectangleForNextRow(padding, Math.max(labelHeight, templateLockControlSize))
            .performingFunctionWithSelf(rowFrame =>
                this.cpvTemplateLockButton.layoutBesideTitleLabelInFrame(this.cpvCodeLabel, rowFrame)
            )
            .rectangleForNextRow(padding * 0.25, fieldHeight)
            .assignedAsFrameOfView(this.cpvCodeTextField)
            .IF(this.cpvCodeValidationMessageLabel.superview === this)
            .rectangleForNextRow(RHSpacing.tiny, this.cpvCodeValidationMessageLabel)
            .assignedAsFrameOfView(this.cpvCodeValidationMessageLabel)
            .ENDIF()
            .IF(this.cpvLockIndicator.superview === this)
            .rectangleForNextRow(RHSpacing.tiny, this.cpvLockIndicator)
            .assignedAsFrameOfView(this.cpvLockIndicator)
            .ENDIF()
            .IF(this.cpvSummationPartReportView.superview == this)
            .rectangleForNextRow(RHSpacing.small, this.cpvSummationPartReportView)
            .assignedAsFrameOfView(this.cpvSummationPartReportView)
            .ENDIF()
            .ENDIF()
            .IF(this.foundCompanyCountLabel.superview === this)
            .rectangleForNextRow(RHSpacing.small, this.foundCompanyCountLabel)
            .assignedAsFrameOfView(this.foundCompanyCountLabel)
            .ENDIF()

            .IF(!this._isCpvHidden || !this._isInvitedCompaniesHidden)
            .rectangleForNextRow(
                RHSpacing.medium,
                RHButtons.compactPrimaryHeight
            )
            .performingFunctionWithSelf(buttonsRowFrame => {
                const buttons: RHButton[] = []
                if (!this._isCpvHidden) {
                    buttons.push(this.openCPVTreeButton)
                }
                if (!this._isInvitedCompaniesHidden) {
                    buttons.push(this.openInvitedCompaniesButton)
                }
                const supplierLockWidth = IF(this.invitedCompaniesTemplateLockButton.superview === this)(
                    () => this.invitedCompaniesTemplateLockButton.widthIncludingVisibilityButton +
                        RHNewProcurementViewTokens.templateLockControl.gap
                ).ELSE(() => 0)
                const availableButtonWidth = Math.max(0,
                    (buttonsRowFrame.width - supplierLockWidth - RHSpacing.medium * (buttons.length - 1)) /
                        buttons.length
                )
                buttonsRowFrame
                    .framesByDistributingViewsAsRow(buttons, RHSpacing.medium,
                        buttons.map(button => Math.min(this._actionButtonWidth(button), availableButtonWidth)))
                    .lastElement
                    .IF(this.invitedCompaniesTemplateLockButton.superview === this)
                    .rectangleForNextColumn(RHNewProcurementViewTokens.templateLockControl.gap, supplierLockWidth)
                    .rectangleWithHeight(templateLockControlSize, 0.5)
                    .performingFunctionWithSelf(frame => this.invitedCompaniesTemplateLockButton.layoutInFrame(frame))
                    .ENDIF()
            })
            .ENDIF()
            .IF(this.invitedCompaniesLockIndicator.superview === this)
            .rectangleForNextRow(RHSpacing.tiny, this.invitedCompaniesLockIndicator)
            .assignedAsFrameOfView(this.invitedCompaniesLockIndicator)
            .ENDIF()
            // ─── Per-part duration ─────────────────────────────────────────────
            // "Copy from above" button: shown below the title when a preceding duration exists.
            .IF(!this._isContractDurationHidden)
            .rectangleForNextRow(padding * 1.5, this.durationSectionTitleLabel)
            .performingFunctionWithSelf(rowFrame =>
                this.contractDurationTemplateLockButton.layoutBesideTitleLabelInFrame(
                    this.durationSectionTitleLabel, rowFrame
                )
            )
            // ── Copy-from-above button (only when preceding duration exists) ──
            .IF(this.copyDurationFromAboveButton.superview == this)
            .rectangleForNextRow(padding * 0.5, RHButtons.compactPrimaryHeight)
            .rectangleWithWidth(bounds.width)
            .rectangleWithWidth(250, 0.5)
            .assignedAsFrameOfView(this.copyDurationFromAboveButton)
            .rectangleWithWidth(bounds.width, 0.5)
            .ENDIF()
            .rectangleForNextRow(padding * 0.5, labelHeight)
            .distributeViewsEquallyAlongWidth(
                [this.durationSpecifiedInDocsButton, this.durationInMonthsButton],
                padding
            )
            .rectangleForNextRow(padding * 0.5, labelHeight)
            .distributeViewsEquallyAlongWidth(
                [this.durationInDaysButton, this.durationAsDateRangeButton],
                padding
            )
            // Conditional value inputs
            .IF(this.contractDurationMonthsLabel.superview == this)
            .rectangleForNextRow(padding, this.contractDurationMonthsLabel)
            .assignedAsFrameOfView(this.contractDurationMonthsLabel)
            .rectangleForNextRow(padding * 0.25, fieldHeight)
            .assignedAsFrameOfView(this.contractDurationMonthsTextField)
            .ELSE_IF(this.contractDurationDaysLabel.superview == this)
            .rectangleForNextRow(padding, this.contractDurationDaysLabel)
            .assignedAsFrameOfView(this.contractDurationDaysLabel)
            .rectangleForNextRow(padding * 0.25, fieldHeight)
            .assignedAsFrameOfView(this.contractDurationDaysTextField)
            .ELSE_IF(this.contractDurationStartDateLabel.superview == this)
            .rectangleForNextRow(padding, this.contractDurationStartDateLabel)
            .assignedAsFrameOfView(this.contractDurationStartDateLabel)
            .rectangleForNextRow(padding * 0.25, fieldHeight)
            .assignedAsFrameOfView(this.contractDurationStartDatePicker)
            .rectangleForNextRow(padding, this.contractDurationEndDateLabel)
            .assignedAsFrameOfView(this.contractDurationEndDateLabel)
            .rectangleForNextRow(padding * 0.25, fieldHeight)
            .assignedAsFrameOfView(this.contractDurationEndDatePicker)
            .ENDIF()
            .IF(this.contractDurationValidationMessageLabel.superview == this)
            .rectangleForNextRow(RHSpacing.tiny, this.contractDurationValidationMessageLabel)
            .assignedAsFrameOfView(this.contractDurationValidationMessageLabel)
            .ENDIF()
            .ENDIF()

    }

}
